import {
  TBitValue,
  TReactSetState,
  TUseTranslationTfn,
} from "../../data/AppType";
import { BIT_VALUE } from "../../utils/AppEnumerations";
import * as Yup from "yup";
import {
  handleError,
  prepareMessageFromParams,
  sweetAlertError,
  sweetAlertSuccess,
} from "../../utils/AppFunctions";
import {
  CREATE_ROLE,
  GET_ALL_CLAIM_GROUP,
  GET_ROLE_BY_ID,
  UPDATE_ROLE,
} from "../../services/Endpoints";
import {
  DEFAULT_STATUS_CODE_SUCCESS,
  MESSAGE_UNKNOWN_ERROR_OCCURRED,
} from "../../utils/AppConstants";
import { IClaims } from "../../data/AppInterface";
import {
  IClaimGroup,
  IClaimOtherGroup,
} from "../claim-group/ClaimGroupController";

export interface IRolesForm {
  id?: string;
  role_name: string;
  claims: string[];
  is_active: TBitValue;
  created_by?: string;
  modified_by?: string;
  created_date?: Date;
  modified_date?: Date;
}

export const ROLES_FIELD_INITIAL_VALUES = {
  role_name: "",
  claims: [],
  is_active: BIT_VALUE.One,
};

export const roleFieldValidationSchema = (t: TUseTranslationTfn) =>
  Yup.object().shape({
    role_name: Yup.string()
      .required(
        prepareMessageFromParams(t("error_message_required"), [
          ["fieldName", t("role_name")],
        ])
      )
      .max(
        50,
        prepareMessageFromParams(t("error_message_max_length"), [
          ["fieldName", t("role_name")],
          ["max", "50"],
        ])
      )
      .min(
        2,
        prepareMessageFromParams(t("error_message_min_length"), [
          ["fieldName", t("role_name")],
          ["min", "2"],
        ])
      ),
  });

export const getAllClaimGroupList = async (
  setClaimGroupList: TReactSetState<IClaimGroup[]>,
  setClaimOtherGroupList: TReactSetState<IClaimOtherGroup[]>,
  activeInactiveValue: string
) => {
  try {
    const claimGroupCode = await GET_ALL_CLAIM_GROUP(activeInactiveValue);
    if (claimGroupCode && claimGroupCode.code === DEFAULT_STATUS_CODE_SUCCESS) {
      setClaimGroupList(claimGroupCode.data.item);
      setClaimOtherGroupList(claimGroupCode.data.other_claims);
    } else {
      setClaimGroupList([]);
      setClaimOtherGroupList([]);
      sweetAlertError({
        message: claimGroupCode.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
      });
    }
  } catch (error: any) {
    handleError(error, "getAllClaimGroupList");
  }
};

export const handleSubmitRole = async (
  values: IRolesForm,
  setLoading: TReactSetState<boolean>,
  id: string | undefined,
  handleClose: (status: boolean) => void
) => {
  setLoading(true);
  try {
    let resultRole;
    if (id) {
      resultRole = await UPDATE_ROLE(values, id);
    } else {
      resultRole = await CREATE_ROLE(values);
    }
    if (resultRole && resultRole.code === DEFAULT_STATUS_CODE_SUCCESS) {
      sweetAlertSuccess({
        message: resultRole.message,
      });
      return handleClose(true);
    } else {
      sweetAlertError({
        message: resultRole?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
      });
    }
  } catch (e: any) {
    handleError(e, "handleSubmitRole");
  } finally {
    setLoading(false);
  }
};

export const fetchRoleById = async (
  id: string,
  handleClose: (status: boolean) => void,
  setRole: TReactSetState<IRolesForm>,
  setAddClaims: TReactSetState<IClaims[]>
) => {
  try {
    const roleData = await GET_ROLE_BY_ID(id);
    if (roleData && roleData.code === DEFAULT_STATUS_CODE_SUCCESS) {
      setRole(roleData.data);
      setAddClaims(roleData.data.claims);
    } else {
      handleClose(false);
      sweetAlertError({
        message: roleData.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
      });
    }
  } catch (e: any) {
    handleClose(false);
    handleError(e, "fetchRoleById");
  }
};
